import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Stack,
  Chip,
  Tooltip,
  useTheme,
  alpha,
  Divider,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { AiChatInterface } from './AiChatInterface';
import { type AiConfigData } from '@/services/aiService';
import { useAuthStore } from '@/store/authStore';

export interface AiAssistantDrawerProps {
  open: boolean;
  onClose: () => void;
}

export function AiAssistantDrawer({ open, onClose }: AiAssistantDrawerProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const [aiConfig, setAiConfig] = useState<AiConfigData | null>(null);
  const clearThreadRef = useRef<(() => void) | null>(null);

  const isPresidentOrVP = user?.roles?.some((r: string) =>
    ['PRESIDENT', 'VICE_PRESIDENT'].includes(r.toUpperCase()),
  );

  const handleOpenFullPage = () => {
    onClose();
    navigate('/ai');
  };

  const handleOpenSettings = () => {
    onClose();
    navigate('/settings/ai-config');
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      elevation={16}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 460, md: 500 },
          maxWidth: '100vw',
          bgcolor: theme.palette.background.default,
          display: 'flex',
          flexDirection: 'column',
          boxShadow:
            theme.palette.mode === 'dark'
              ? '-8px 0 32px rgba(0,0,0,0.6)'
              : '-8px 0 32px rgba(0,0,0,0.12)',
        },
      }}
      sx={{
        zIndex: (t) => t.zIndex.drawer + 2,
      }}
    >
      {/* Drawer Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor:
            theme.palette.mode === 'dark'
              ? alpha(theme.palette.background.paper, 0.95)
              : alpha(theme.palette.primary.main, 0.04),
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
          flexShrink: 0,
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: '0.95rem' }}>
              AI Assistant
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              Executive Society Assistant
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={0.5} alignItems="center">
          {aiConfig?.hasApiKey && (
            <Chip
              label={aiConfig.provider}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ height: 22, fontSize: '0.68rem', fontWeight: 600, mr: 0.5 }}
            />
          )}

          {isPresidentOrVP && (
            <Tooltip title="AI Settings">
              <IconButton size="small" onClick={handleOpenSettings} aria-label="Open AI Settings">
                <SettingsOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="Reset Conversation">
            <IconButton
              size="small"
              onClick={() => clearThreadRef.current?.()}
              aria-label="Reset Conversation"
            >
              <RestartAltIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Open Full Page">
            <IconButton
              size="small"
              onClick={handleOpenFullPage}
              aria-label="Open AI Assistant full page"
            >
              <OpenInNewIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 18, alignSelf: 'center' }} />

          <Tooltip title="Close">
            <IconButton size="small" onClick={onClose} aria-label="Close AI Assistant">
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Drawer Chat Body */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <AiChatInterface
          mode="drawer"
          onNavigateToConfig={handleOpenSettings}
          onClearThreadRef={clearThreadRef}
          onConfigLoaded={setAiConfig}
        />
      </Box>
    </Drawer>
  );
}
